import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { signUp, createProfile, uploadPhoto } from '../../lib/supabase';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const isMountedRef = useRef(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    gender: '',
    lookingFor: [] as string[],
    country: '',
    city: '',
    bio: '',
    interests: [] as string[],
    photoFiles: [] as File[],
  });

  const countries = [
    'Türkiye', 'ABD', 'İngiltere', 'Almanya', 'Fransa', 'İtalya', 'İspanya', 
    'Rusya', 'Japonya', 'Çin', 'Hindistan', 'Brezilya', 'Kanada', 'Avustralya',
    'Hollanda', 'Belçika', 'İsviçre', 'Avusturya', 'İsveç', 'Norveç', 'Danimarka'
  ];

  const genderOptions = [
    { value: 'male', label: 'Erkek' },
    { value: 'female', label: 'Kadın' },
    { value: 'gay', label: 'Gay' },
    { value: 'lesbian', label: 'Lezbiyen' },
    { value: 'trans', label: 'Trans' },
    { value: 'other', label: 'Diğer' },
  ];

  const lookingForOptions = [
    { value: 'male', label: 'Erkek' },
    { value: 'female', label: 'Kadın' },
    { value: 'gay', label: 'Gay' },
    { value: 'lesbian', label: 'Lezbiyen' },
    { value: 'trans', label: 'Trans' },
    { value: 'everyone', label: 'Herkes' },
  ];

  const interestOptions = [
    'Seyahat', 'Müzik', 'Spor', 'Sinema', 'Kitap', 'Yemek', 
    'Sanat', 'Teknoloji', 'Doğa', 'Dans', 'Fotoğrafçılık', 'Yoga'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleInterest = (interest: string) => {
    setFormData({
      ...formData,
      interests: formData.interests.includes(interest)
        ? formData.interests.filter(i => i !== interest)
        : [...formData.interests, interest]
    });
  };

  const toggleLookingFor = (option: string) => {
    setFormData({
      ...formData,
      lookingFor: formData.lookingFor.includes(option)
        ? formData.lookingFor.filter(i => i !== option)
        : [...formData.lookingFor, option]
    });
  };

  // Cleanup preview URLs on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      photoPreviewUrls.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (err) {
          // Ignore errors
        }
      });
    };
  }, []);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files).slice(0, 6 - formData.photoFiles.length);
      const newUrls = newFiles.map(file => URL.createObjectURL(file));
      
      setFormData({
        ...formData,
        photoFiles: [...formData.photoFiles, ...newFiles]
      });
      
      setPhotoPreviewUrls([...photoPreviewUrls, ...newUrls]);
    }
  };

  const removePhoto = (index: number) => {
    const urlToRevoke = photoPreviewUrls[index];
    if (urlToRevoke) {
      try {
        URL.revokeObjectURL(urlToRevoke);
      } catch (err) {
        // Ignore errors
      }
    }
    
    setFormData(prev => ({
      ...prev,
      photoFiles: prev.photoFiles.filter((_, i) => i !== index)
    }));
    
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMountedRef.current) return;
    
    setError('');
    
    // Age verification
    if (step === 1 && formData.birthDate) {
      const age = calculateAge(formData.birthDate);
      if (age < 18) {
        if (isMountedRef.current) setError('18 yaşından küçükler bu platformu kullanamaz.');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        if (isMountedRef.current) setError('Şifreler eşleşmiyor.');
        return;
      }

      if (formData.password.length < 8) {
        if (isMountedRef.current) setError('Şifre en az 8 karakter olmalıdır.');
        return;
      }
    }

    // Looking for validation
    if (step === 2 && formData.lookingFor.length === 0) {
      if (isMountedRef.current) setError('En az bir seçenek seçmelisiniz.');
      return;
    }

    // Photo verification
    if (step === 3 && formData.photoFiles.length === 0) {
      if (isMountedRef.current) setError('En az 1 profil fotoğrafı yüklemeniz gerekiyor.');
      return;
    }

    // Interests validation
    if (step === 4 && formData.interests.length < 3) {
      if (isMountedRef.current) setError('En az 3 ilgi alanı seçmelisiniz.');
      return;
    }

    if (step < 4) {
      if (isMountedRef.current) setStep(step + 1);
    } else {
      // Final submission
      if (isMountedRef.current) setLoading(true);
      try {
        console.log('🚀 Kayıt işlemi başlıyor...');
        
        // 1. Create auth user
        console.log('1️⃣ Kullanıcı oluşturuluyor...');
        const { data: authData, error: authError } = await signUp(formData.email, formData.password);
        
        if (authError) {
          console.error('❌ Auth hatası:', authError);
          throw authError;
        }
        if (!authData.user) {
          console.error('❌ Kullanıcı verisi yok');
          throw new Error('Kullanıcı oluşturulamadı');
        }

        console.log('✅ Kullanıcı oluşturuldu:', authData.user.id);

        // 2. Upload photos
        console.log('2️⃣ Fotoğraflar yükleniyor... Toplam:', formData.photoFiles.length);
        const photoUrls: string[] = [];
        
        for (let i = 0; i < formData.photoFiles.length; i++) {
          const file = formData.photoFiles[i];
          console.log(`📸 Fotoğraf ${i + 1}/${formData.photoFiles.length} yükleniyor...`);
          
          const { data: photoUrl, error: photoError } = await uploadPhoto(authData.user.id, file);
          
          if (photoError) {
            console.error(`❌ Fotoğraf ${i + 1} yükleme hatası:`, photoError);
            throw new Error(`Fotoğraf yüklenemedi: ${photoError.message}`);
          }
          
          if (photoUrl) {
            photoUrls.push(photoUrl);
            console.log(`✅ Fotoğraf ${i + 1} yüklendi:`, photoUrl);
          }
        }

        if (photoUrls.length === 0) {
          console.error('❌ Hiç fotoğraf yüklenemedi');
          throw new Error('Fotoğraflar yüklenemedi. Lütfen tekrar deneyin.');
        }

        console.log('✅ Tüm fotoğraflar yüklendi:', photoUrls.length);

        // 3. Create profile
        console.log('3️⃣ Profil oluşturuluyor...');
        const profileData = {
          id: authData.user.id,
          full_name: formData.name,
          email: formData.email,
          birth_date: formData.birthDate,
          gender: formData.gender,
          looking_for: formData.lookingFor,
          country: formData.country,
          city: formData.city,
          bio: formData.bio,
          interests: formData.interests,
          photos: photoUrls,
        };

        const { error: profileError } = await createProfile(profileData);
        if (profileError) {
          console.error('❌ Profil oluşturma hatası:', profileError);
          throw profileError;
        }

        console.log('✅ Profil oluşturuldu');

        // 4. Wait for auth sync
        console.log('4️⃣ Auth senkronizasyonu bekleniyor...');
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Success - navigate
        console.log('🎉 Kayıt tamamlandı! Yönlendiriliyor...');
        if (isMountedRef.current) {
          navigate('/arama', { replace: true });
        }
      } catch (err: any) {
        console.error('💥 Kayıt hatası:', err);
        if (isMountedRef.current) {
          setError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
          setLoading(false);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Adım {step}/4</span>
              <span className="text-sm font-medium text-gray-700">{Math.round((step / 4) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 4) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {step === 1 && 'Hesap Oluştur'}
                {step === 2 && 'Kişisel Bilgiler'}
                {step === 3 && 'Fotoğraflarınız'}
                {step === 4 && 'İlgi Alanları'}
              </h1>
              <p className="text-sm text-gray-600">
                {step === 1 && 'Başlamak için temel bilgilerinizi girin (18+ yaş sınırı)'}
                {step === 2 && 'Sizi daha iyi tanımak için birkaç bilgi daha'}
                {step === 3 && 'En az 1, en fazla 6 fotoğraf yükleyin (Zorunlu)'}
                {step === 4 && 'İlgi alanlarınızı seçin ve profilinizi tamamlayın'}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Step 1: Basic Info */}
              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad Soyad
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="ornek@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şifre
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="En az 8 karakter"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şifre Tekrar
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="Şifrenizi tekrar girin"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doğum Tarihi (18+ yaş sınırı)
                    </label>
                    <input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      required
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">18 yaşından küçükler kayıt olamaz</p>
                  </div>
                </div>
              )}

              {/* Step 2: Personal Info */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kimliğiniz
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    >
                      <option value="">Seçiniz</option>
                      {genderOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Kimleri Arıyorsunuz? (Birden fazla seçebilirsiniz)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {lookingForOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleLookingFor(option.value)}
                          className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap cursor-pointer ${
                            formData.lookingFor.includes(option.value)
                              ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white border-transparent'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-rose-500'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ülke
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    >
                      <option value="">Ülke Seçiniz</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Şehir
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="Şehir adı"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hakkında
                    </label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                      placeholder="Kendinizi kısaca tanıtın..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.bio.length}/500 karakter</p>
                  </div>
                </div>
              )}

              {/* Step 3: Photos */}
              {step === 3 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Fotoğraflarınızı Yükleyin (En az 1, en fazla 6 - Zorunlu)
                    </label>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {photoPreviewUrls.map((url, index) => (
                        <div key={index} className="relative aspect-square">
                          <img
                            src={url}
                            alt={`Fotoğraf ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                      ))}
                      
                      {formData.photoFiles.length < 6 && (
                        <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-rose-500 transition-colors">
                          <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                          <span className="text-xs text-gray-600">Fotoğraf Ekle</span>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex gap-3">
                        <i className="ri-information-line text-yellow-600 text-xl"></i>
                        <div className="text-sm text-yellow-800">
                          <p className="font-medium mb-1">Önemli Bilgiler:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            <li>En az 1 fotoğraf yüklemeniz zorunludur</li>
                            <li>Fotoğraflar net ve büyük boyutlu olmalıdır</li>
                            <li>Yüzünüzün net görüldüğü fotoğraflar tercih edilir</li>
                            <li>Uygunsuz içerik yüklemeyiniz</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Interests */}
              {step === 4 && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      İlgi Alanlarınızı Seçin (En az 3)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {interestOptions.map((interest) => (
                        <button
                          key={interest}
                          type="button"
                          onClick={() => toggleInterest(interest)}
                          className={`px-4 py-3 text-sm font-medium rounded-lg border-2 transition-all whitespace-nowrap cursor-pointer ${
                            formData.interests.includes(interest)
                              ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white border-transparent'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-rose-500'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      {formData.interests.length} ilgi alanı seçildi
                    </p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-4 mt-8">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    Geri
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-semibold rounded-lg hover:from-rose-600 hover:to-pink-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {loading ? 'Yükleniyor...' : step === 4 ? 'Tamamla' : 'Devam Et'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Zaten hesabınız var mı?{' '}
                <Link to="/" className="text-rose-500 font-medium hover:text-rose-600 cursor-pointer">
                  Giriş Yap
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterPage;
